import React, { useState } from "react";
import styled from "styled-components";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { stripePromise } from "../../config/stripe";
import { paymentService, PaymentIntentRequest } from "../../api/paymentService";
import {
  getPaymentErrorMessage,
  PaymentError,
} from "../../utils/paymentErrorMessages";

interface PaymentFormProps {
  orderData: PaymentIntentRequest;
  onPaymentSuccess: (orderId: string) => void;
  onPaymentError: (error: string) => void;
}

const PaymentFormContainer = styled.div`
  max-width: 500px;
  margin: 0 auto;
  padding: 2rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  text-align: center;
  color: #333;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #555;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #e97d7d;
  }
`;

const CardElementContainer = styled.div`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: #e97d7d;
  }
`;

const PayButton = styled.button`
  background: #e97d7d;
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 1rem;

  &:hover:not(:disabled) {
    background: #d66b6b;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #fdf2f2;
  border-radius: 4px;
  border: 1px solid #fecaca;
`;

const SuccessMessage = styled.div`
  color: #059669;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #f0fdf4;
  border-radius: 4px;
  border: 1px solid #bbf7d0;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #e97d7d;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Card Element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#9e2146",
    },
  },
  hidePostalCode: true, // Hide the built-in postal code field to use our custom one
};

const PaymentFormContent: React.FC<PaymentFormProps> = ({
  orderData,
  onPaymentSuccess,
  onPaymentError,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Initialize postal code from billing or shipping address
  const initialPostalCode =
    orderData.billingAddress?.zipCode ||
    orderData.shippingAddress?.zipCode ||
    "";
  const [postalCode, setPostalCode] = useState<string>(initialPostalCode);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Create payment intent
      const paymentIntentResponse = await paymentService.createPaymentIntent(
        orderData
      );

      // Get postal code from billing or shipping address
      const postalCodeValue =
        postalCode ||
        orderData.billingAddress?.zipCode ||
        orderData.shippingAddress?.zipCode ||
        "";

      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } =
        await stripe.confirmCardPayment(paymentIntentResponse.clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement)!,
            billing_details: {
              name: orderData.customerName,
              email: orderData.customerEmail,
              address: {
                postal_code: postalCodeValue.replace(/\s+/g, "").toUpperCase(), // Remove spaces and uppercase for consistency
              },
            },
          },
        });

      if (stripeError) {
        // Extract error code and type from Stripe error
        const paymentError: PaymentError = {
          code: stripeError.code,
          type: stripeError.type,
          message: stripeError.message || "Payment failed",
        };
        const userFriendlyMessage = getPaymentErrorMessage(paymentError);
        setError(userFriendlyMessage);
        onPaymentError(userFriendlyMessage);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        // Confirm payment on backend
        await paymentService.confirmPayment({
          paymentIntentId: paymentIntent.id,
          orderId: paymentIntentResponse.orderId,
        });

        setSuccess("Payment successful! Your order has been placed.");
        onPaymentSuccess(paymentIntentResponse.orderId);
      } else {
        // Payment intent exists but status is not succeeded
        const status = paymentIntent?.status || "unknown";
        let errorMessage = "Payment was not successful.";

        if (status === "requires_payment_method") {
          errorMessage =
            "Your payment method was declined. Please contact your bank or try again with a different card.";
        } else if (status === "requires_confirmation") {
          errorMessage = "Payment requires confirmation. Please try again.";
        } else if (status === "requires_action") {
          errorMessage =
            "Your card requires additional authentication. Please complete the authentication and try again.";
        } else if (status === "processing") {
          errorMessage =
            "Your payment is being processed. Please wait a moment and check your order status.";
        } else if (status === "canceled") {
          errorMessage =
            "Your payment was canceled. Please try again if you wish to complete your order.";
        }

        setError(errorMessage);
        onPaymentError(errorMessage);
      }
    } catch (err) {
      // Try to extract error information from the error object
      let paymentError: PaymentError | string =
        err instanceof Error ? err.message : "An unexpected error occurred";

      // If error is an object with code/type, use it
      if (err && typeof err === "object" && "code" in err) {
        paymentError = {
          code: (err as any).code,
          type: (err as any).type,
          message:
            err instanceof Error ? err.message : "An unexpected error occurred",
        };
      }

      const userFriendlyMessage = getPaymentErrorMessage(paymentError);
      setError(userFriendlyMessage);
      onPaymentError(userFriendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PaymentFormContainer>
      <Title>Payment Information</Title>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Card Number</Label>
          <CardElementContainer>
            <CardElement options={cardElementOptions} />
          </CardElementContainer>
        </FormGroup>

        <FormGroup>
          <Label>Postal Code / ZIP Code</Label>
          <Input
            type="text"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            placeholder={
              orderData.billingAddress?.country === "Canada" ||
              orderData.shippingAddress?.country === "Canada"
                ? "e.g., M5H 2N2"
                : "e.g., 12345 or M5H 2N2"
            }
            required
            maxLength={10}
            style={{ textTransform: "uppercase" }}
          />
        </FormGroup>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <PayButton type="submit" disabled={!stripe || loading}>
          {loading && <LoadingSpinner />}
          {loading
            ? "Processing..."
            : `Pay $${orderData.totalAmount.toFixed(2)}`}
        </PayButton>
      </Form>
    </PaymentFormContainer>
  );
};

const PaymentForm: React.FC<PaymentFormProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentFormContent {...props} />
    </Elements>
  );
};

export default PaymentForm;
